import type { RowDraft } from './draft.ts'
import type { ExtensionMatch, ExtensionProblem, RegisteredExtension, RowView } from './extension.ts'

export type RowStatus = 'empty' | 'incomplete' | 'needs-details' | 'blocked' | 'ready'

export type SplitProblemReason = 'target' | 'amount' | 'currency'

export interface RowResolution {
  readonly status: RowStatus
  readonly extension: RegisteredExtension | undefined
  readonly match: ExtensionMatch | undefined
  readonly draft: unknown
  readonly problems: readonly ExtensionProblem[]
  readonly splitProblems: ReadonlyMap<string, SplitProblemReason>
}

/** A row the user has typed into. A carried-forward date and account alone do not count. */
export function isTouched(row: RowDraft): boolean {
  return (
    row.splits.some((split) => split.targetId !== undefined || split.amount !== null) ||
    (row.payee ?? '').trim() !== '' ||
    row.memo.trim() !== '' ||
    row.tags.length > 0
  )
}

/** A currency mismatch is the extension's to judge once one owns how the row posts. */
export function coreSplitProblems(
  view: RowView,
  checkCurrency: boolean,
): ReadonlyMap<string, SplitProblemReason> {
  const problems = new Map<string, SplitProblemReason>()
  view.row.splits.forEach((split, index) => {
    const target = view.counters[index]
    const sameAccount =
      split.targetId === view.row.accountId && split.targetLedgerId === view.row.accountLedgerId
    if (split.targetId === undefined || sameAccount) {
      problems.set(split.id, 'target')
    } else if (split.amount === null) {
      problems.set(split.id, 'amount')
    } else if (
      checkCurrency &&
      target?.currency !== undefined &&
      view.currency !== undefined &&
      target.currency !== view.currency
    ) {
      problems.set(split.id, 'currency')
    }
  })
  return problems
}

export function resolveRow(
  view: RowView,
  extension: RegisteredExtension | undefined,
  match: ExtensionMatch | undefined,
  draft: unknown,
  splitsNotCarried: string,
): RowResolution {
  const ownsPosting = extension !== undefined && extension.submit.shape !== 'attach'
  const splitProblems = coreSplitProblems(view, !ownsPosting)
  const problems: ExtensionProblem[] =
    extension === undefined || match === undefined
      ? []
      : [...extension.problems(match as never, draft as never, view)]
  if (extension !== undefined && !extension.carries.has('splits') && view.row.splits.length > 1) {
    problems.push({ severity: 'needs-details', message: splitsNotCarried })
  }
  const base = { extension, match, draft, problems, splitProblems }
  if (!isTouched(view.row)) return { ...base, status: 'empty' }
  if (problems.some((problem) => problem.severity === 'blocked')) return { ...base, status: 'blocked' }
  const complete =
    view.row.accountId !== undefined &&
    view.currency !== undefined &&
    view.row.splits.length > 0 &&
    splitProblems.size === 0
  if (!complete) return { ...base, status: 'incomplete' }
  if (problems.length > 0) return { ...base, status: 'needs-details' }
  return { ...base, status: 'ready' }
}
